// components/customer/CustomerCard.tsx
import { useAppTheme } from "@/hooks/useAppTheme";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { deleteCustomer, updateCustomer } from "@/store/slices/customerSlice";
import { Ionicons } from "@expo/vector-icons";
import * as Clipboard from "expo-clipboard";
import { router } from "expo-router";
import { useState } from "react";
import {
  Alert,
  Linking,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";

interface Customer {
  id: number;
  fullName: string;
  phone: string;
  address?: string;
  balance?: string;
  status: "active" | "inactive";
  Area?: {
    id: number;
    name: string;
  };
}

interface CustomerCardProps {
  customer: Customer;
  onRefresh: () => void;
}

export default function CustomerCard({
  customer,
  onRefresh,
}: CustomerCardProps) {
  const { colors, spacing } = useAppTheme();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  const isActive = customer.status === "active";
  const isAdmin = user?.userType === "Admin" || user?.userType === "Owner";

  const handleEdit = () => router.push(`/(app)/customers/${customer.id}`);

  const handleCall = () => {
    Linking.openURL(`tel:${customer.phone}`);
  };

  const handleCopyPhone = async () => {
    await Clipboard.setStringAsync(customer.phone);
    Alert.alert("Copied", "Phone number copied to clipboard");
  };

  const handleToggleStatus = async () => {
    const newStatus = isActive ? "inactive" : "active";
    Alert.alert(
      `${newStatus} Customer`,
      `Are you sure you want to ${newStatus} ${customer.fullName}?`,
      [
        { text: "Cancel", style: "cancel" },
        {
          text: newStatus.toUpperCase(),
          onPress: async () => {
            setLoading(true);
            const result = await dispatch(
              updateCustomer({ id: customer.id, data: { status: newStatus } }),
            );
            if (updateCustomer.fulfilled.match(result)) onRefresh();
            setLoading(false);
          },
        },
      ],
    );
  };

  const handleDelete = () => {
    Alert.alert("Delete Customer", `Delete ${customer.fullName}?`, [
      { text: "Cancel", style: "cancel" },
      {
        text: "Delete",
        style: "destructive",
        onPress: async () => {
          setLoading(true);
          const result = await dispatch(deleteCustomer(customer.id));
          if (deleteCustomer.fulfilled.match(result)) onRefresh();
          setLoading(false);
        },
      },
    ]);
  };

  const styles = StyleSheet.create({
    card: {
      backgroundColor: colors.background,
      borderRadius: 12,
      padding: spacing.md,
      marginBottom: spacing.md,
      borderWidth: 1,
      borderColor: colors.border,
    },
    header: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: spacing.xs,
    },
    name: { fontSize: 16, fontWeight: "600", color: colors.text },
    phoneRow: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      marginTop: 2,
    },
    phone: { fontSize: 14, color: colors.textLight, flex: 1 },
    phoneActions: {
      flexDirection: "row",
      gap: spacing.sm,
    },
    phoneIcon: {
      padding: 4,
    },
    address: { fontSize: 14, color: colors.textLight, marginTop: 2 },
    balance: {
      fontSize: 14,
      fontWeight: "500",
      color: colors.primary,
      marginTop: 2,
    },
    statusBadge: {
      flexDirection: "row",
      alignItems: "center",
      marginTop: spacing.sm,
    },
    statusDot: { width: 8, height: 8, borderRadius: 4, marginRight: 6 },
    statusText: { fontSize: 12, fontWeight: "500" },
    actions: {
      flexDirection: "row",
      marginTop: spacing.md,
      paddingTop: spacing.sm,
      borderTopWidth: 1,
      borderTopColor: colors.border,
    },
    actionButton: {
      flex: 1,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      paddingVertical: 6,
    },
    actionText: { fontSize: 12, marginLeft: 4, color: colors.textLight },
  });

  return (
    <View style={styles.card}>
      <View style={styles.header}>
        <Text style={styles.name}>{customer.fullName}</Text>
        <TouchableOpacity onPress={handleEdit} disabled={loading}>
          <Ionicons name="chevron-forward" size={20} color={colors.textLight} />
        </TouchableOpacity>
      </View>

      {/* Mobile Number with Call & Copy buttons */}
      <View style={styles.phoneRow}>
        <Text style={styles.phone}>📞 {customer.phone}</Text>
        <View style={styles.phoneActions}>
          <TouchableOpacity onPress={handleCall} style={styles.phoneIcon}>
            <Ionicons name="call-outline" size={18} color={colors.primary} />
          </TouchableOpacity>
          <TouchableOpacity onPress={handleCopyPhone} style={styles.phoneIcon}>
            <Ionicons name="copy-outline" size={18} color={colors.textLight} />
          </TouchableOpacity>
        </View>
      </View>

      {/* Address */}
      {customer.address && (
        <Text style={styles.address}>
          📍 {customer.address} ({customer?.Area?.name})
        </Text>
      )}

      {/* Balance */}
      {customer.balance !== undefined && (
        <Text style={styles.balance}>
          💰 Balance: ₹{parseFloat(customer.balance).toFixed(2)}
        </Text>
      )}

      {/* Status Badge */}
      <View style={styles.statusBadge}>
        <View
          style={[
            styles.statusDot,
            { backgroundColor: isActive ? "#22c55e" : "#ef4444" },
          ]}
        />
        <Text
          style={[
            styles.statusText,
            { color: isActive ? "#22c55e" : "#ef4444" },
          ]}
        >
          {isActive ? "Active" : "Inactive"}
        </Text>
      </View>

      {/* Action Buttons – only show Deactivate & Delete for admin/owner */}
      <View style={styles.actions}>
        <TouchableOpacity style={styles.actionButton} onPress={handleEdit}>
          <Ionicons name="create-outline" size={18} color={colors.primary} />
          <Text style={styles.actionText}>Edit</Text>
        </TouchableOpacity>

        {isAdmin && (
          <>
            <TouchableOpacity
              style={styles.actionButton}
              onPress={handleToggleStatus}
            >
              <Ionicons
                name={isActive ? "eye-off-outline" : "eye-outline"}
                size={18}
                color="#f59e0b"
              />
              <Text style={styles.actionText}>
                {isActive ? "Deactivate" : "Activate"}
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.actionButton}
              onPress={handleDelete}
            >
              <Ionicons name="trash-outline" size={18} color={colors.danger} />
              <Text style={[styles.actionText, { color: colors.danger }]}>
                Delete
              </Text>
            </TouchableOpacity>
          </>
        )}
      </View>
    </View>
  );
}
